<template>
  <el-dialog
    v-el-drag-dialog
    :visible.sync="innerVisible"
    :title="$t('FinanceManage.applyRecallCharge')"
    width="1000px"
    :close-on-click-modal="false"
    class="app-main-inner"
    :append-to-body="true"
  >
    <div class="section grid-wrapper">
      <cargo-ag-grid
        config-module="13011103"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :row-data="rowData"
        style="width: 100%;min-height: 350px;"
        @gridReady="params=>{gridApi=params.api}"
      />
    </div>
    <div slot="footer">
      <el-button type="primary" size="mini" @click="saveEvent">{{ $t('SeaExport.Confirm') }}</el-button>
      <el-button size="mini" @click="cancel">{{ $t('SeaExport.Cancel') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'
import ColumnUtils from '@/utils/ColumnUtils'
import * as ChargeConstants from '@/class/cargo/model/parameters/ChargeConstants'
import CargoAgGridCommonTextareaEditor from '@/components/Cargo/Common/CargoAgGridCommonTextareaEditor/index'
import { deepClone } from '@/utils'

export default {
  name: 'SyncChargeApplyRecall',
  props: {
    visible: { type: Boolean, required: true },
    recallCharges: { type: Array, required: true },
    applyCallBack: { type: Function, default: () => null }
  },
  data() {
    return {
      innerVisible: false,
      gridOptions: {
        rowSelection: 'multiple',
        singleClickEdit: true,
        suppressCellSelection: true,
        context: {
          componentParent: this
        },
        frameworkComponents: {
          CargoAgGridCommonTextareaEditor
        }
      },
      columnDefs: [
        {
          headerName: this.$t('FinanceManage.receivablePayable'),
          colId: 'receivable',
          field: 'receivable',
          width: 120,
          valueFormatter: params => {
            return ColumnUtils.chargeReceivableLabelFun(params.value)
          }
        },
        {
          headerName: this.$t('FinanceManage.sender'),
          field: 'sender',
          width: 80,
          valueFormatter: params => {
            return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Office_All, 'officeId', params.value, 'code')
          }
        },
        {
          headerName: this.$t('FinanceManage.receiver'),
          field: 'receiver',
          width: 80,
          valueFormatter: params => {
            return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Office_All, 'officeId', params.value, 'code')
          }
        },
        {
          headerName: this.$t('FinanceManage.charge_chargeItem'),
          colId: 'chargeItemId',
          field: 'chargeItemId',
          width: 180,
          valueFormatter: params => {
            return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_ChargeItem, 'itemId', params.value, 'zhName')
          }
        },
        {
          headerName: this.$t('FinanceManage.currency'),
          colId: 'currency',
          field: 'currency',
          width: 180
        },
        {
          headerName: this.$t('FinanceManage.charge_taxIncludedPrice'),
          colId: 'amount',
          field: 'amount',
          width: 180,
          valueFormatter: params => {
            return this.$numeral(params.value).format('0.00')
          }
        },
        {
          headerName: this.$t('FinanceManage.charge_taxExcludedPrice'),
          colId: 'excludingTaxAmount',
          field: 'excludingTaxAmount',
          width: 180,
          valueFormatter: params => {
            return this.$numeral(params.value).format('0.00')
          }
        },
        {
          headerName: this.$t('FinanceManage.charge_taxAmount'),
          colId: 'taxAmount',
          field: 'taxAmount',
          width: 180,
          valueFormatter: params => {
            return this.$numeral(params.value).format('0.00')
          }
        },
        {
          headerName: this.$t('FinanceManage.charge_taxRate'),
          colId: 'taxRate',
          field: 'taxRate',
          width: 180,
          valueFormatter: params => {
            return this.$numeral(params.value).format('0.00')
          }
        },
        {
          headerName: this.$t('FinanceManage.charge_unitPrice'),
          colId: 'unitRate',
          field: 'unitRate',
          width: 180,
          valueFormatter: params => {
            return this.$numeral(params.value).format('0.000')
          }
        },
        {
          headerName: this.$t('FinanceManage.adChargeInfo_quantity'),
          colId: 'quantity',
          field: 'quantity',
          width: 180,
          valueFormatter: params => {
            return this.$numeral(params.value).format('0.000')
          }
        },
        {
          headerName: this.$t('FinanceManage.charge_exchangeRate'),
          colId: 'exchangeRate',
          field: 'exchangeRate',
          width: 180,
          valueFormatter: params => {
            return this.$numeral(params.value).format('0.000000')
          }
        },
        {
          headerName: this.$t('FinanceManage.charge_somesticOrInternational'),
          colId: 'oversea',
          field: 'oversea',
          width: 180,
          valueFormatter: params => {
            return params.value ? this.$t('SeaExport.overseas') : this.$t('SeaExport.internal')
          }
        },
        {
          headerName: this.$t('FinanceManage.syncProgress'),
          colId: 'syncProgress',
          field: 'syncProgress',
          width: 180,
          cellRenderer: params => {
            const text = ColumnUtils.chargeSyncProgrssLabelFunc(params.value)
            if (params.value === ChargeConstants.THE_OTHER_PARTY_HAS_REFUSED.code) {
              return `<span style="color: red">${text}</span>`
            }
            return text
          }
        },
        {
          headerName: this.$t('FinanceManage.processingStatus'),
          colId: 'handlingStatus',
          field: 'handlingStatus',
          width: 180,
          valueFormatter: params => {
            let text = params.value ? this.$t('CargoMsg.Cargo_Handled') : this.$t('FinanceManage.pending')
            if (params.data.syncProgress !== ChargeConstants.TO_BE_CONFIRMED_BY_US.code && params.data.syncProgress !== ChargeConstants.TO_BE_CONFIRMED_BY_OTHER_PARTY.code) {
              text += `（${params.data.read ? this.$t('FinanceManage.alreadyRead') : this.$t('FinanceManage.notRead')}）`
            }
            return text
          }
        },
        {
          headerName: this.$t('FinanceManage.sendingTime'),
          colId: 'dateCreated',
          field: 'dateCreated',
          width: 180
        },
        {
          headerName: this.$t('FinanceManage.auditOperationTime'),
          colId: 'verifiedTime',
          field: 'verifiedTime',
          width: 180
        },
        {
          headerName: this.$t('SeaExport.WarehouseNo'),
          colId: 'warehouseNo',
          field: 'warehouseNo',
          width: 180
        },
        {
          headerName: 'SO No.',
          colId: 'soNo',
          field: 'soNo',
          width: 180
        },
        {
          headerName: this.$t('SeaExport.Unit'),
          colId: 'measure',
          field: 'measure',
          width: 180
        },
        {
          headerName: this.$t('CargoMsg.Cargo_DenialReason'),
          colId: 'rejectReason',
          field: 'rejectReason',
          width: 180
        },
        {
          headerName: this.$t('SeaExport.source'),
          colId: 'syncSource',
          field: 'syncSource',
          width: 180,
          cellRenderer: params => {
            let text = this.$t('SeaExport.ConsignationSI')
            if (params.value === 1) {
              text = this.$t('SeaExport.Consignation')
            }
            return text
          }
        },
        {
          headerName: this.$t('FinanceManage.recallState'),
          colId: 'recallProgress',
          field: 'recallProgress',
          width: 180,
          cellRenderer: params => {
            return ColumnUtils.chargeSyncRecallStateLabelFunc(params.value)
          }
        },
        {
          headerName: this.$t('FinanceManage.recallCause'),
          colId: 'recallCause',
          field: 'recallCause',
          width: 180,
          editable: true,
          cellEditor: 'CargoAgGridCommonTextareaEditor'
        }
      ],
      rowData: [],
      gridApi: null
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(newVal) {
        this.innerVisible = newVal
        if (newVal) {
          this.refreshCharge = false
          this.init()
        }
      }
    },
    innerVisible(newVal) {
      this.$emit('update:visible', newVal)
      if (!newVal) {
        this.$emit('returnCall', this.rowData)
        this.rowData = []
      }
    }
  },
  methods: {
    init() {
      this.rowData = deepClone(this.recallCharges)
    },
    cancel() {
      this.rowData = []
      this.$emit('close')
    },
    saveEvent() {
      if (this.rowData && this.applyCallBack) {
        this.applyCallBack(this.rowData)
      }
      this.cancel()
    }
  }
}
</script>

<style lang="scss" scoped>
.charge-apply-recall {
  label {
    font-size: 12px;
    font-weight: normal;
  }
  .el-select {
    width: 100%;
  }
}
</style>
